import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import DatePicker from '../../components/ui/DatePicker';
import Textarea from '../../components/ui/Textarea';
import { listWorkflowCategories, listDocumentTypes } from '../../api/workflow';
import { listLeadSources, listNationalities } from '../../api/masters';
import { createLead, uploadLeadDocument } from '../../api/leads';
import { pushToast } from '../../components/ui/toastBus';
import { OptionCards, VEHICLE_OPTIONS, LOCATION_OPTIONS, LICENSE_OPTIONS } from '../../components/candidates/OptionCards';

const EMPTY_PROFILE = {
  full_name: '',
  email: '',
  contact_no: '',
  whatsapp_no: '',
  home_country_contact_no: '',
  nationality_id: '',
  date_of_birth: '',
  passport_no: '',
  passport_expiry_date: '',
  passport_expiry_overridden: false,
  old_emirates_id_no: '',
  referred_agent_name: '',
  has_rta_permit: false,
  rta_permit_details: '',
  visa_type: '',
  visa_validity: '',
  uid_no: '',
  uae_license_no: '',
  uae_license_expiry: '',
  home_license_no: '',
  home_license_expiry: '',
  lead_source_id: '',
  notes: '',
};

export default function LeadRegister() {
  const navigate = useNavigate();
  const categoriesQuery = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories });
  const documentTypesQuery = useQuery({ queryKey: ['document-types'], queryFn: listDocumentTypes });
  const sourcesQuery = useQuery({ queryKey: ['lead-sources'], queryFn: () => listLeadSources() });
  const nationalitiesQuery = useQuery({ queryKey: ['nationalities'], queryFn: () => listNationalities() });

  const [vehicleType, setVehicleType] = useState(null);
  const [location, setLocation] = useState(null);
  const [licenseType, setLicenseType] = useState(null);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [errors, setErrors] = useState({});
  const [documentFiles, setDocumentFiles] = useState({});
  const [uploading, setUploading] = useState(false);

  const category = useMemo(
    () =>
      categoriesQuery.data?.find(
        (c) => c.vehicle_type === vehicleType && c.location === location && c.license_type === licenseType
      ),
    [categoriesQuery.data, vehicleType, location, licenseType]
  );

  const registrationRequiredDocs = (category?.steps?.find((s) => s.code === 'registration')?.required_documents || []).map((d) => d.code);
  const docTypeName = (code) => documentTypesQuery.data?.find((t) => t.code === code)?.name || code;

  const passportWarning =
    profile.passport_expiry_date &&
    new Date(profile.passport_expiry_date) < new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);

  const mutation = useMutation({
    mutationFn: (payload) => createLead(payload),
    onSuccess: async (lead) => {
      const filesToUpload = Object.entries(documentFiles).filter(([, file]) => file);

      if (filesToUpload.length > 0) {
        setUploading(true);
        for (const [code, file] of filesToUpload) {
          const docType = documentTypesQuery.data?.find((t) => t.code === code);
          if (!docType) continue;

          const formData = new FormData();
          formData.append('file', file);
          formData.append('document_type_id', docType.id);

          try {
            await uploadLeadDocument(lead.id, formData);
          } catch (err) {
            const reason = err.response?.data?.message || err.response?.data?.errors?.file?.[0];
            pushToast({
              type: 'error',
              message: `Could not upload ${docType.name}${reason ? `: ${reason}` : ''}. You can add it later from the lead's profile.`,
            });
          }
        }
        setUploading(false);
      }

      pushToast({ type: 'success', message: `Lead registered: ${lead.reference_no}` });
      navigate(`/leads/${lead.id}`);
    },
    onError: (err) => setErrors(err.response?.data?.errors || {}),
  });

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setProfile((p) => ({ ...p, [field]: value }));
  };

  const setDocumentFile = (code) => (e) => {
    setDocumentFiles((prev) => ({ ...prev, [code]: e.target.files[0] || null }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!category) return;
    const payload = { ...profile, workflow_category_id: category.id };
    if (!payload.lead_source_id) delete payload.lead_source_id;
    if (location !== 'inside_uae') {
      delete payload.visa_type;
      delete payload.visa_validity;
      delete payload.uid_no;
    }
    mutation.mutate(payload);
  };

  const fieldError = (field) => errors[field]?.[0];

  return (
    <div className="w-full">
      <PageHeader title="Register Lead" description="Capture a prospect now — full profile details can be filled in later, before sending to Candidate." />

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Category</h3>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Vehicle Type</h4>
                <OptionCards options={VEHICLE_OPTIONS} value={vehicleType} onChange={setVehicleType} />
              </div>
              <div>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Location</h4>
                <OptionCards options={LOCATION_OPTIONS} value={location} onChange={setLocation} />
              </div>
              <div>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">License Type</h4>
                <OptionCards options={LICENSE_OPTIONS} value={licenseType} onChange={setLicenseType} />
              </div>
            </div>

            {vehicleType && location && licenseType && !category && (
              <p className="mt-4 text-sm text-red-600">No matching workflow category found.</p>
            )}
          </div>

          <div className="border-t border-gray-100 pt-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Lead Details</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Input label="Full Name" required value={profile.full_name} onChange={set('full_name')} error={fieldError('full_name')} className="md:col-span-2" />
              <Input label="Contact No" required value={profile.contact_no} onChange={set('contact_no')} error={fieldError('contact_no')} />
              <Select label="Source" value={profile.lead_source_id} onChange={set('lead_source_id')} error={fieldError('lead_source_id')}>
                <option value="">Select…</option>
                {(sourcesQuery.data || []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
              <Input label="Email" type="email" value={profile.email} onChange={set('email')} error={fieldError('email')} />
              <Input label="WhatsApp No" value={profile.whatsapp_no} onChange={set('whatsapp_no')} error={fieldError('whatsapp_no')} />
              <Input label="Home Country / Emergency Contact No" value={profile.home_country_contact_no} onChange={set('home_country_contact_no')} error={fieldError('home_country_contact_no')} />
              <Select label="Nationality" value={profile.nationality_id} onChange={set('nationality_id')} error={fieldError('nationality_id')}>
                <option value="">Select…</option>
                {(nationalitiesQuery.data || []).map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name}
                  </option>
                ))}
              </Select>
              <DatePicker label="Date of Birth" value={profile.date_of_birth} onChange={set('date_of_birth')} error={fieldError('date_of_birth')} />
              <Input label="Passport No" value={profile.passport_no} onChange={set('passport_no')} error={fieldError('passport_no')} />
              <div>
                <DatePicker
                  label="Passport Expiry Date"
                  value={profile.passport_expiry_date}
                  onChange={set('passport_expiry_date')}
                  error={fieldError('passport_expiry_date')}
                />
                {passportWarning && (
                  <div className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <p>Passport expires in less than 1 year — sending to Candidate will be blocked until this is resolved.</p>
                    <label className="mt-1 flex items-center gap-1.5">
                      <input type="checkbox" checked={profile.passport_expiry_overridden} onChange={set('passport_expiry_overridden')} />
                      Allow sending anyway
                    </label>
                  </div>
                )}
              </div>
              <Input label="Old Emirates ID No" value={profile.old_emirates_id_no} onChange={set('old_emirates_id_no')} />
              <Input label="Referred Agent's Name" value={profile.referred_agent_name} onChange={set('referred_agent_name')} />
            </div>
            <p className="mt-3 text-xs text-gray-500">
              Only name and contact number are required to save a lead. Everything else — including passport and
              license details — must be complete before it can be sent to Candidate.
            </p>
          </div>

          {location === 'inside_uae' && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Visa Details (Inside UAE)</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Select label="Visa Type" value={profile.visa_type} onChange={set('visa_type')} error={fieldError('visa_type')}>
                  <option value="">Select…</option>
                  <option value="visit">Visit</option>
                  <option value="employment">Employment</option>
                </Select>
                <DatePicker label="Visa Validity" value={profile.visa_validity} onChange={set('visa_validity')} />
                <Input label="UID No" value={profile.uid_no} onChange={set('uid_no')} />
              </div>
            </div>
          )}

          <div className="border-t border-gray-100 pt-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">License Details</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {licenseType === 'uae' && (
                <>
                  <Input label="UAE DL No" value={profile.uae_license_no} onChange={set('uae_license_no')} error={fieldError('uae_license_no')} />
                  <DatePicker label="UAE DL Expiry" value={profile.uae_license_expiry} onChange={set('uae_license_expiry')} error={fieldError('uae_license_expiry')} />
                </>
              )}
              {licenseType === 'gcc_home' && (
                <>
                  <Input label="Home/GCC DL No" value={profile.home_license_no} onChange={set('home_license_no')} error={fieldError('home_license_no')} />
                  <DatePicker label="Home/GCC DL Expiry" value={profile.home_license_expiry} onChange={set('home_license_expiry')} error={fieldError('home_license_expiry')} />
                  <Input label="UAE DL No (if held)" value={profile.uae_license_no} onChange={set('uae_license_no')} />
                  <DatePicker label="UAE DL Expiry (if held)" value={profile.uae_license_expiry} onChange={set('uae_license_expiry')} />
                </>
              )}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input type="checkbox" checked={profile.has_rta_permit} onChange={set('has_rta_permit')} />
              Holds an RTA permit with Dubai companies?
            </label>
            {profile.has_rta_permit && (
              <Textarea
                className="mt-2"
                placeholder="Details"
                value={profile.rta_permit_details}
                onChange={set('rta_permit_details')}
              />
            )}
          </div>

          <div className="border-t border-gray-100 pt-4">
            <Textarea label="Notes" placeholder="Any context worth keeping about this lead" value={profile.notes} onChange={set('notes')} />
          </div>

          {category && registrationRequiredDocs.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="mb-1 text-sm font-semibold text-gray-900">Required Documents</h3>
              <p className="mb-3 text-xs text-gray-500">
                Attach these now, or add them later from the lead's profile — before it can be sent to Candidate.
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {registrationRequiredDocs.map((code) => (
                  <div key={code}>
                    <label className="mb-1 block text-sm font-medium text-gray-700">{docTypeName(code)}</label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
                      onChange={setDocumentFile(code)}
                      className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary hover:file:bg-primary-light"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button type="submit" loading={mutation.isPending || uploading} disabled={!category}>
              Register Lead
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
